<template>
  <div class="d-flex flex-column fill-height px-10">
    <FlyoutMenuHead :selectedItem="selectedItem" type="reset"  @close="closeFlyout" />
    <v-container fluid class="px-0 scrollable-container mt-10">
      <!-- Hidden dummy fields — prevents browser password manager from targeting real fields -->
      <input type="text" autocomplete="username" style="display:none" aria-hidden="true" />
      <v-row dense class="pa-0 mb-6">
        <v-col>
          <v-text-field
            v-model="username"
            label="Username"
            outlined
            dense
            hide-details
            maxlength="48"
            autocomplete="off"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row dense class="pa-0 mb-0">
        <v-col>
          <v-text-field
            v-model="password"
            label="New Password"
            type="text"
            class="password-field"
            :class="{ 'password-visible': showPassword }"
            outlined
            dense
            required
            maxlength="30"
            spellcheck="false"
            autocomplete="off"
            :append-icon="showPassword ? icons.mdiEye : icons.mdiEyeOff"
            @click:append="showPassword = !showPassword"
            :rules="passwordRules"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row dense class="pa-0 mb-0">
        <v-col>
          <v-text-field
            v-model="confirmPassword"
            label="Confirm Password"
            type="text"
            class="password-field"
            :class="{ 'password-visible': showConfirmPassword }"
            outlined
            dense
            required
            maxlength="30"
            spellcheck="false"
            autocomplete="off"
            :append-icon="showConfirmPassword ? icons.mdiEye : icons.mdiEyeOff"
            @click:append="showConfirmPassword = !showConfirmPassword"
            :rules="confirmPasswordRules"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>
    <FormFooter
      type="reset"
      :currentStep="1"
      :totalSteps="1"
      @save="handleSubmit"
      @cancel="handleCancel"
      :disabled="!password || !confirmPassword"
    />
  </div>
</template>

<script>
import FlyoutMenuHead from '../FlyoutMenuHead.vue';
import FormFooter from './FormFooter.vue';
import { mapActions, mapGetters } from 'vuex';
import { mdiEye, mdiEyeOff } from '@mdi/js';

export default {
  name: 'ResetOperatorCredentials',
  components: {
    FlyoutMenuHead,
    FormFooter
  },
  data() {
    return {
      username: '',
      password: '',
      confirmPassword: '',
      operator: null,
      selectedItem: { name: 'Reset Operator' },
      showPassword: false,
      showConfirmPassword: false,
      icons: {
        mdiEye: mdiEye,
        mdiEyeOff: mdiEyeOff
      }
    };
  },
  computed: {
    ...mapGetters('operators', ['getOperator', 'isLoading', 'getErrors']),
    loading() {
      return this.isLoading.resettingCreds;
    },
    error() {
      return this.getErrors.resettingCreds;
    },
    confirmPasswordRules() {
      return [
        v => !!v || 'Confirm password is required',
        v => v === this.password || 'Passwords do not match'
      ];
    },
    passwordRules() {
      return [
        v => !!v || 'Password is required',
        v => v.length >= 8 || 'Password must be at least 8 characters long',
        v => /[A-Z]/.test(v) || 'Password must contain at least one uppercase letter',
        v => /\d/.test(v) || 'Password must contain at least one number',
        // eslint-disable-next-line no-useless-escape
        v => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>?/]/.test(v) || 'Password must contain at least one special character'
      ];
    },
  },
  async created() {
    const uuid = this.$route.params.uuid;
    try {
      this.operator = await this.fetchOperator(uuid);
      this.username = this.operator.username;
    } catch (e) {
      console.error('Failed to fetch operator', e);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.$el.querySelectorAll('input').forEach(el => {
        el.setAttribute('readonly', '');
        el.setAttribute('data-1p-ignore', '');
        el.setAttribute('data-lpignore', 'true');
        el.addEventListener('focus', () => el.removeAttribute('readonly'), { once: true });
      });
    });
  },
  methods: {
    ...mapActions('operators', ['fetchOperator', 'resetOperatorCredentials']),
    ...mapActions('global', ['addAlert']),
    async handleSubmit() {
      try {
        await this.resetOperatorCredentials({
          operator_uuid: this.operator.uuid,
          payload: { username: this.username, password: this.password }
        });
        this.addAlert({ type: 'success', message: 'Credentials reset successfully' });
        // Clear fields before navigation — prevents Chrome/Edge from triggering save-password prompt
        this.username = '';
        this.password = '';
        this.confirmPassword = '';
        await this.$nextTick();
        this.closeFlyout();
      } catch (e) {
        this.addAlert({ type: 'error', message: 'Failed to reset credentials: ' + (e.message || e) });
      }
    },
    handleCancel() {
      this.closeFlyout();
    },
    closeFlyout() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
/* Mask password field — type="text" prevents Chrome from detecting a password field */
.password-field ::v-deep input {
  -webkit-text-security: disc;
}
.password-field.password-visible ::v-deep input {
  -webkit-text-security: none;
}
</style>